import { useState } from "react";
import { useTheme } from "../hooks/useTheme";
import {
  Mail,
  Linkedin,
  Github,
  Send,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { submitContactForm } from "../utils/contactApi";

const Contact = () => {
  const { isDarkMode } = useTheme();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    project: "",
    message: "",
  });

  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSuccess: false,
    isError: false,
    message: "",
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset form status
    setFormStatus({
      isSubmitting: true,
      isSuccess: false,
      isError: false,
      message: "",
    });

    try {
      // Submit form via serverless function
      await submitContactForm(formData);

      // Success - reset form and show success message
      setFormData({ name: "", email: "", project: "", message: "" });
      setFormStatus({
        isSubmitting: false,
        isSuccess: true,
        isError: false,
        message:
          "Thank you for your message! We'll get back to you within 24 hours.",
      });

      // Clear success message after 5 seconds
      setTimeout(() => {
        setFormStatus((prev) => ({ ...prev, isSuccess: false, message: "" }));
      }, 5000);
    } catch (error) {
      console.error("Form submission error:", error);
      setFormStatus({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        message:
          "Sorry, there was an error sending your message. Please try again or contact us directly.",
      });

      // Clear error message after 7 seconds
      setTimeout(() => {
        setFormStatus((prev) => ({ ...prev, isError: false, message: "" }));
      }, 7000);
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-20 md:py-24 lg:py-28 relative">
      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-20 relative z-10">
        <div className="w-full text-center mb-12 sm:mb-16 block">
          <h2
            className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 w-full transition-colors duration-700 ${
              isDarkMode ? "text-white" : "text-graphite-darkest"
            }`}
          >
            Let's Work Together
          </h2>
          <p
            className={`text-lg sm:text-xl lg:text-2xl max-w-4xl mx-auto w-full transition-colors duration-700 ${
              isDarkMode ? "text-gray-200" : "text-graphite-medium"
            }`}
          >
            Ready to bring your project to life? Let's discuss your ideas and
            create something amazing.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-5 gap-8 sm:gap-12 lg:gap-16 w-full">
          <div className="xl:col-span-2">
            <h3
              className={`text-xl sm:text-2xl lg:text-3xl font-semibold mb-4 transition-colors duration-700 ${
                isDarkMode ? "text-white" : "text-graphite-darkest"
              }`}
            >
              Get in Touch
            </h3>
            <p
              className={`text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 leading-relaxed transition-colors duration-700 ${
                isDarkMode ? "text-gray-200" : "text-graphite-medium"
              }`}
            >
              We're always excited to work on new projects and collaborate with
              fellow creators.
            </p>

            <div className="flex flex-col gap-6">
              <div
                className={`group flex items-center p-4 rounded-lg transition-all duration-300 cursor-pointer relative overflow-hidden ${
                  isDarkMode
                    ? "bg-graphite-dark/50 border border-graphite-medium/30 hover:bg-graphite-medium/30 hover:border-graphite-light/50"
                    : "bg-white border border-graphite-light/50 hover:bg-graphite-lightest hover:border-graphite-medium/50"
                }`}
              >
                {/* Graphite shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-graphite-shine/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-graphite-shine"></div>

                <div
                  className={`mr-4 transition-all duration-300 relative z-10 ${
                    isDarkMode
                      ? "text-gray-200 group-hover:text-white"
                      : "text-graphite-medium group-hover:text-graphite-dark"
                  }`}
                >
                  <Mail size={24} />
                </div>
                <div className="relative z-10">
                  <h4
                    className={`font-semibold transition-colors duration-700 ${
                      isDarkMode ? "text-white" : "text-graphite-darkest"
                    }`}
                  >
                    Email
                  </h4>
                  <p
                    className={`text-sm transition-colors duration-700 ${
                      isDarkMode ? "text-gray-200" : "text-graphite-medium"
                    }`}
                  >
                    Get in touch via email
                  </p>
                </div>
              </div>

              <div
                className={`group flex items-center p-4 rounded-lg transition-all duration-300 cursor-pointer relative overflow-hidden ${
                  isDarkMode
                    ? "bg-graphite-dark/50 border border-graphite-medium/30 hover:bg-graphite-medium/30 hover:border-graphite-light/50"
                    : "bg-white border border-graphite-light/50 hover:bg-graphite-lightest hover:border-graphite-medium/50"
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-graphite-shine/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-graphite-shine"></div>

                <div
                  className={`mr-4 transition-all duration-300 relative z-10 ${
                    isDarkMode
                      ? "text-gray-200 group-hover:text-white"
                      : "text-graphite-medium group-hover:text-graphite-dark"
                  }`}
                >
                  <Linkedin size={24} />
                </div>
                <div className="relative z-10">
                  <h4
                    className={`font-semibold transition-colors duration-700 ${
                      isDarkMode ? "text-white" : "text-graphite-darkest"
                    }`}
                  >
                    LinkedIn
                  </h4>
                  <p
                    className={`text-sm transition-colors duration-700 ${
                      isDarkMode ? "text-gray-200" : "text-graphite-medium"
                    }`}
                  >
                    Connect with me professionally
                  </p>
                </div>
              </div>

              <div
                className={`group flex items-center p-4 rounded-lg transition-all duration-300 cursor-pointer relative overflow-hidden ${
                  isDarkMode
                    ? "bg-graphite-dark/50 border border-graphite-medium/30 hover:bg-graphite-medium/30 hover:border-graphite-light/50"
                    : "bg-white border border-graphite-light/50 hover:bg-graphite-lightest hover:border-graphite-medium/50"
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-graphite-shine/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-graphite-shine"></div>

                <div
                  className={`mr-4 transition-all duration-300 relative z-10 ${
                    isDarkMode
                      ? "text-gray-200 group-hover:text-white"
                      : "text-graphite-medium group-hover:text-graphite-dark"
                  }`}
                >
                  <Github size={24} />
                </div>
                <div className="relative z-10">
                  <h4
                    className={`font-semibold transition-colors duration-700 ${
                      isDarkMode ? "text-white" : "text-graphite-darkest"
                    }`}
                  >
                    GitHub
                  </h4>
                  <p
                    className={`text-sm transition-colors duration-700 ${
                      isDarkMode ? "text-gray-200" : "text-graphite-medium"
                    }`}
                  >
                    Explore our projects and code
                  </p>
                </div>
              </div>
            </div>
          </div>

          <form
            className={`xl:col-span-3 p-6 sm:p-8 lg:p-10 rounded-xl border transition-all duration-700 relative overflow-hidden ${
              isDarkMode
                ? "bg-graphite-dark/50 border-graphite-medium/30 backdrop-blur-sm"
                : "bg-white border-graphite-light/50"
            }`}
            onSubmit={handleSubmit}
          >
            {/* Status Message */}
            {(formStatus.isSuccess || formStatus.isError) && (
              <div
                className={`mb-6 p-4 rounded-lg flex items-center gap-3 relative z-10 ${
                  formStatus.isSuccess
                    ? isDarkMode
                      ? "bg-green-900/50 border border-green-700/50 text-green-200"
                      : "bg-green-50 border border-green-200 text-green-800"
                    : isDarkMode
                      ? "bg-red-900/50 border border-red-700/50 text-red-200"
                      : "bg-red-50 border border-red-200 text-red-800"
                }`}
              >
                {formStatus.isSuccess ? (
                  <CheckCircle size={20} className="flex-shrink-0" />
                ) : (
                  <AlertCircle size={20} className="flex-shrink-0" />
                )}
                <p className="text-sm">{formStatus.message}</p>
              </div>
            )}
            {/* Subtle graphite texture overlay */}
            <div
              className={`absolute inset-0 opacity-${isDarkMode ? "20" : "10"} transition-opacity duration-700`}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-transparent via-graphite-medium/5 to-transparent animate-graphite-grain"></div>
            </div>

            <div className="mb-6 relative z-10">
              <label
                htmlFor="name"
                className={`block font-medium mb-2 transition-colors duration-700 ${
                  isDarkMode ? "text-white" : "text-graphite-darkest"
                }`}
              >
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className={`w-full px-4 py-3 border rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 ${
                  isDarkMode
                    ? "bg-graphite-darkest border-graphite-medium/50 text-white placeholder-gray-400 focus:ring-graphite-light focus:border-graphite-light"
                    : "bg-graphite-lightest border-graphite-light/50 text-graphite-darkest placeholder-graphite-medium/50 focus:ring-graphite-medium focus:border-graphite-medium"
                }`}
              />
            </div>

            <div className="mb-6 relative z-10">
              <label
                htmlFor="email"
                className={`block font-medium mb-2 transition-colors duration-700 ${
                  isDarkMode ? "text-white" : "text-graphite-darkest"
                }`}
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={`w-full px-4 py-3 border rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 ${
                  isDarkMode
                    ? "bg-graphite-darkest border-graphite-medium/50 text-white placeholder-gray-400 focus:ring-graphite-light focus:border-graphite-light"
                    : "bg-graphite-lightest border-graphite-light/50 text-graphite-darkest placeholder-graphite-medium/50 focus:ring-graphite-medium focus:border-graphite-medium"
                }`}
              />
            </div>

            <div className="mb-6 relative z-10">
              <label
                htmlFor="project"
                className={`block font-medium mb-2 transition-colors duration-700 ${
                  isDarkMode ? "text-white" : "text-graphite-darkest"
                }`}
              >
                Project Type
              </label>
              <select
                id="project"
                name="project"
                value={formData.project}
                onChange={handleChange}
                required
                className={`w-full px-4 py-3 border rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 ${
                  isDarkMode
                    ? "bg-graphite-darkest border-graphite-medium/50 text-white focus:ring-graphite-light focus:border-graphite-light"
                    : "bg-graphite-lightest border-graphite-light/50 text-graphite-darkest focus:ring-graphite-medium focus:border-graphite-medium"
                }`}
              >
                <option value="">Select a project type</option>
                <option value="web-app">Web Application</option>
                <option value="game">Game Development</option>
                <option value="web3">Web3 Solution</option>
                <option value="mobile">Mobile App</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="mb-6 relative z-10">
              <label
                htmlFor="message"
                className={`block font-medium mb-2 transition-colors duration-700 ${
                  isDarkMode ? "text-white" : "text-graphite-darkest"
                }`}
              >
                Message
              </label>
              <textarea
                id="message"
                name="message"
                rows="5"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your project..."
                required
                className={`w-full px-4 py-3 border rounded-lg resize-y transition-all duration-300 focus:outline-none focus:ring-2 ${
                  isDarkMode
                    ? "bg-graphite-darkest border-graphite-medium/50 text-white placeholder-gray-400 focus:ring-graphite-light focus:border-graphite-light"
                    : "bg-graphite-lightest border-graphite-light/50 text-graphite-darkest placeholder-graphite-medium/50 focus:ring-graphite-medium focus:border-graphite-medium"
                }`}
              ></textarea>
            </div>

            <button
              type="submit"
              disabled={formStatus.isSubmitting}
              className={`group w-full font-semibold py-3 px-6 rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 relative overflow-hidden flex items-center justify-center gap-2 ${
                formStatus.isSubmitting
                  ? isDarkMode
                    ? "bg-graphite-medium/50 text-gray-400 cursor-not-allowed"
                    : "bg-graphite-light/50 text-graphite-medium cursor-not-allowed"
                  : isDarkMode
                    ? "bg-gradient-to-r from-graphite-medium to-graphite-dark text-white hover:from-graphite-light hover:to-graphite-medium focus:ring-graphite-light shadow-lg shadow-graphite-darker/50"
                    : "bg-gradient-to-r from-graphite-dark to-graphite-darker text-white hover:from-graphite-darker hover:to-graphite-darkest focus:ring-graphite-medium shadow-lg shadow-graphite-medium/30"
              }`}
            >
              <span className="relative z-10 flex items-center gap-2">
                {formStatus.isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send Message
                  </>
                )}
              </span>
              {!formStatus.isSubmitting && (
                <div className="absolute inset-0 bg-gradient-to-r from-graphite-shine/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-graphite-shine"></div>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
